import type { Card } from '@campfire/cards';
import type { Seat } from '@campfire/engine';
import type { CheatView } from '@campfire/game-cheat';
import type { UnoCard, UnoColor, UnoEvent, UnoView } from '@campfire/game-uno';
import type { TableEvent, TableView } from '@campfire/game-table';
import type { HoldemView } from '@campfire/game-holdem';
import type { GoFishView } from '@campfire/game-go-fish';
import type { OldMaidView } from '@campfire/game-old-maid';
import type { WarView } from '@campfire/game-war';
import type { StateMessage } from '@campfire/net';
import { cardBack, cardFace, cardLabel, pickTray, pile, rankPlural } from './cards.ts';
import { confirmButton, h } from './dom.ts';
import { chutesUi } from './ui/chutes.ts';
import { sorryUi } from './ui/sorry.ts';
import { parcheesiUi } from './ui/parcheesi.ts';
import { candylandUi } from './ui/candyland.ts';
import { yahtzeeUi } from './ui/yahtzee.ts';
import { mastermindUi } from './ui/mastermind.ts';
import { checkersUi } from './ui/checkers.ts';
import { battleshipUi } from './ui/battleship.ts';
import { chessUi } from './ui/chess.ts';
import { farkleUi } from './ui/farkle.ts';
import { liarsDiceUi } from './ui/liars-dice.ts';
import { heartsUi } from './ui/hearts.ts';
import { spadesUi } from './ui/spades.ts';
import { euchreUi } from './ui/euchre.ts';
import { crazyEightsUi } from './ui/crazy-eights.ts';
import { ginUi } from './ui/gin.ts';
import { rummy500Ui } from './ui/rummy500.ts';
import { applyOrder, moveBlock, type Move } from './order.ts';

export interface TableCtx {
  readonly msg: StateMessage;
  readonly names: readonly string[];
  readonly mySeat: Seat | null;
  /** The seat words address as "You"; null in pass & play, where everyone shares the phone (FR-UX-12). */
  readonly you: Seat | null;
  readonly myTurn: boolean;
  act(action: { type: string } & Record<string, unknown>): void;
  /** UI-only state that survives re-renders (e.g. a selected rank). */
  readonly local: { get<T>(key: string): T | undefined; set(key: string, v: unknown): void };
}

export interface GameUi {
  readonly id: string;
  /** Hot-seat shows a hand-off screen between players only when hands are secret. */
  readonly privateHands: boolean;
  render(ctx: TableCtx): HTMLElement;
  /** Seat summary chip extras, e.g. card counts and books. */
  seatInfo(ctx: TableCtx, seat: Seat): string;
}

/** "You" or the seat's name. Exported for per-game UI modules in ./ui/. */
export const name = (ctx: TableCtx, s: Seat) => (s === ctx.you ? 'You' : (ctx.names[s] ?? `Seat ${s + 1}`));

/** Everyone else's hands as face-down fans, so every screen reads as a shared table. */
export function others(ctx: TableCtx, counts: readonly number[], extra: (s: Seat) => HTMLElement | null = () => null): HTMLElement {
  return h(
    'div.others',
    { 'data-testid': 'others' },
    counts.flatMap((n, s) =>
      s === ctx.mySeat
        ? []
        : [
            h(
              `div.other${(ctx.msg.waitingOn ?? ctx.msg.activeSeats).includes(s) ? '.active' : ''}`,
              { 'data-testid': `other-${s}` },
              h('div.other-name', {}, ctx.names[s] ?? `Seat ${s + 1}`),
              h('div.mini-fan', {}, Array.from({ length: Math.min(n, 12) }, () => cardBack({ small: true }))),
              extra(s),
            ),
          ],
    ),
  );
}

// ---------------- War ----------------
const warUi: GameUi = {
  id: 'war',
  privateHands: false,
  seatInfo: (ctx, s) => `${(ctx.msg.view as WarView).pileCounts[s] ?? 0} cards`,
  render(ctx) {
    const v = ctx.msg.view as WarView;
    const side = (s: Seat) =>
      h(
        'div.war-side',
        { 'data-testid': `war-side-${s}` },
        h('div.label', {}, name(ctx, s)),
        pile(v.pileCounts[s] ?? 0, `war-pile-${s}`),
        h(
          'div.battle',
          {},
          v.faceDownCounts[s] ? h('div.facedown', {}, Array.from({ length: v.faceDownCounts[s] ?? 0 }, () => cardBack({ small: true }))) : null,
          (v.faceUp[s] ?? []).map((c, i, arr) => cardFace(c, { small: i < arr.length - 1, testid: i === arr.length - 1 ? `war-up-${s}` : undefined })),
        ),
        lastSide(s),
      );
    const canFlip = ctx.mySeat !== null && ctx.myTurn;
    const showLast = v.lastBattle && v.faceUp[0].length === 0 && v.faceUp[1].length === 0;
    const lastSide = (s: Seat) =>
      showLast && v.lastBattle
        ? h('div.battle.last', {}, (v.lastBattle.faceUp[s] ?? []).map((c, i, arr) => cardFace(c, { small: i < arr.length - 1, testid: i === arr.length - 1 ? `war-last-${s}` : undefined })))
        : null;
    return h(
      'div.war',
      {},
      side(0),
      h(
        'div.war-center',
        {},
        showLast && v.lastBattle
          ? h('div.event', { 'data-testid': 'event' }, `${name(ctx, v.lastBattle.winner)} ${v.lastBattle.winner === ctx.you ? 'take' : 'takes'} ${v.lastBattle.taken} cards`)
          : v.faceUp[0].length > 1
            ? h('div.event', { 'data-testid': 'event' }, 'WAR! Flip again to settle it')
            : null,
        v.wars > 0 ? h('div.badge', {}, `Wars: ${v.wars}`) : null,
        h('div.muted', {}, `Round ${v.rounds + 1}`),
        canFlip ? h('button.primary.big', { 'data-testid': 'flip', onclick: () => ctx.act({ type: 'flip' }) }, (v.faceDownCounts[ctx.mySeat ?? 0] ?? 0) > 0 ? 'Flip for the war!' : 'Flip') : null,
      ),
      side(1),
    );
  },
};

// ---------------- Go Fish ----------------
const goFishUi: GameUi = {
  id: 'go-fish',
  privateHands: true,
  seatInfo: (ctx, s) => {
    const v = ctx.msg.view as GoFishView;
    const books = v.books[s] ?? [];
    return `${v.handCounts[s] ?? 0} cards · ${books.length} book${books.length === 1 ? '' : 's'}`;
  },
  render(ctx) {
    const v = ctx.msg.view as GoFishView;
    const selected = ctx.local.get<string>('gofish-rank');
    const hand = v.hand ?? [];
    const validSel = selected && hand.some((c) => c.rank === selected) ? selected : undefined;

    const last = v.last;
    const lastText = last
      ? `${name(ctx, last.asker)} asked ${name(ctx, last.target)} for ${rankPlural(last.rank)} — ` +
        (last.given > 0
          ? `got ${last.given}.`
          : last.drawn
            ? `Go fish! Drew ${cardLabel(last.drawn)}${last.luckyDraw ? ' — lucky!' : '.'}`
            : last.fished && v.ocean === 0
              ? 'Go fish! The ocean is empty.'
              : 'Go fish!') +
        (last.booked.length ? ` Booked ${last.booked.map(rankPlural).join(', ')}!` : '')
      : 'Ask another player for a rank you hold.';
    const hint = ctx.myTurn ? (validSel ? `Now tap who to ask for ${rankPlural(validSel)}` : 'Tap a card in your hand, then tap who to ask') : null;

    const opponents = ctx.names
      .map((n, s) => ({ n, s }))
      .filter(({ s }) => s !== ctx.mySeat)
      .map(({ s }) =>
        h(
          'button.opponent',
          {
            'data-testid': `ask-${s}`,
            disabled: !(ctx.myTurn && validSel && (v.handCounts[s] ?? 0) > 0),
            onclick: () => {
              if (!validSel) return;
              ctx.local.set('gofish-rank', undefined);
              ctx.act({ type: 'ask', target: s, rank: validSel });
            },
          },
          h('strong', {}, name(ctx, s)),
          h('span.muted', {}, `${v.handCounts[s] ?? 0} cards`),
          validSel && ctx.myTurn ? h('span', {}, `Ask for ${rankPlural(validSel)}`) : null,
        ),
      );

    return h(
      'div.gofish',
      {},
      others(ctx, v.handCounts, (s) => ((v.books[s]?.length ?? 0) > 0 ? h('div.muted', {}, `Books: ${(v.books[s] ?? []).map(rankPlural).join(', ')}`) : null)),
      h('div.row', {}, h('div.ocean', { 'data-testid': 'ocean' }, pile(v.ocean), h('div.muted', {}, 'Ocean'))),
      h('div.event', { 'data-testid': 'event' }, lastText),
      hint ? h('div.hint', { 'data-testid': 'hint' }, hint) : null,
      ctx.mySeat !== null ? h('div.opponents', {}, opponents) : null,
      v.hand
        ? h(
            'div.hand',
            { 'data-testid': 'hand' },
            hand.map((c) =>
              cardFace(c, {
                selected: c.rank === validSel,
                onclick: ctx.myTurn ? () => ctx.local.set('gofish-rank', c.rank === validSel ? undefined : c.rank) : undefined,
              }),
            ),
          )
        : null,
      ctx.mySeat !== null && (v.books[ctx.mySeat]?.length ?? 0) > 0
        ? h('div.books', {}, 'Your books: ', (v.books[ctx.mySeat] ?? []).map(rankPlural).join(', '))
        : null,
    );
  },
};

// ---------------- Old Maid ----------------
const oldMaidUi: GameUi = {
  id: 'old-maid',
  privateHands: true,
  seatInfo: (ctx, s) => {
    const n = (ctx.msg.view as OldMaidView).handCounts[s] ?? 0;
    return n === 0 ? 'out — safe!' : `${n} cards`;
  },
  render(ctx) {
    const v = ctx.msg.view as OldMaidView;
    const last = v.last;
    const lastText = last
      ? `${name(ctx, last.drawer)} drew ${last.card ? cardLabel(last.card) : 'a card'} from ${name(ctx, last.from)}` +
        (last.paired ? ` — a pair of ${rankPlural(last.paired)}!` : '.')
      : 'Draw a card from the player shown. Avoid the Old Maid!';
    const from = v.drawFrom;
    const fan =
      from !== null
        ? h(
            'div.fan-wrap',
            {},
            h('div.muted', {}, ctx.myTurn ? `Pick a card from ${name(ctx, from)}` : `${name(ctx, v.turn)} is drawing from ${name(ctx, from)}`),
            h(
              'div.fan',
              { 'data-testid': 'fan' },
              Array.from({ length: v.handCounts[from] ?? 0 }, (_, index) =>
                cardBack({
                  testid: `fan-${index}`,
                  label: `card ${index + 1}`,
                  onclick: ctx.myTurn ? () => ctx.act({ type: 'draw', index }) : undefined,
                }),
              ),
            ),
          )
        : null;
    const pairs = v.discards.length / 2;
    return h(
      'div.oldmaid',
      {},
      ctx.mySeat === null ? others(ctx, v.handCounts, (s) => ((v.handCounts[s] ?? 0) === 0 ? h('div.muted', {}, 'Out — safe!') : null)) : null,
      h('div.event', { 'data-testid': 'event' }, lastText),
      fan,
      h('div.muted', {}, `${pairs} pair${pairs === 1 ? '' : 's'} discarded`),
      v.hand ? h('div.hand', { 'data-testid': 'hand' }, v.hand.map((c: Card) => cardFace(c))) : null,
    );
  },
};

// ---------------- Cheat ----------------
const cheatUi: GameUi = {
  id: 'cheat',
  privateHands: true,
  seatInfo: (ctx, s) => `${(ctx.msg.view as CheatView).handCounts[s] ?? 0} cards`,
  render(ctx) {
    const v = ctx.msg.view as CheatView;
    const hand = v.hand ?? [];
    const picked = (ctx.local.get<string[]>('cheat-sel') ?? []).filter((id) => hand.some((c) => c.id === id));
    const plural = (n: number, rank: string) => (n === 1 ? `one ${rank === 'A' ? 'Ace' : rankPlural(rank).replace(/s$/, '')}` : `${n} ${rankPlural(rank)}`);
    const last = v.last;
    const lastText = !last
      ? `${name(ctx, v.turn)} starts with Aces. Play cards face down and say they are the rank called — truthfully or not.`
      : last.kind === 'claim'
        ? `${name(ctx, last.seat)} ${last.seat === ctx.you ? 'say' : 'says'}: “${plural(last.count, last.rank)}.”`
        : last.kind === 'accepted'
          ? `Nobody called ${name(ctx, last.seat)} on ${plural(last.count, last.rank)}.`
          : `${name(ctx, last.caller)} called Cheat on ${name(ctx, last.claimant)} — ` +
            (last.truthful ? 'it was true! ' : 'caught lying! ') +
            `${name(ctx, last.taker)} ${last.taker === ctx.you ? 'pick' : 'picks'} up ${last.taken} card${last.taken === 1 ? '' : 's'}.`;
    const claim = v.claim;
    const mayCall = !!claim && ctx.mySeat !== null && claim.waiting.includes(ctx.mySeat);
    const myPlay = ctx.myTurn && !claim;
    const toggle = (id: string) => ctx.local.set('cheat-sel', picked.includes(id) ? picked.filter((x) => x !== id) : picked.length < v.maxPlay ? [...picked, id] : picked);
    return h(
      'div.cheat',
      {},
      others(ctx, v.handCounts),
      h(
        'div.row',
        {},
        h('div.ocean', { 'data-testid': 'pile' }, pile(v.pileCount), h('div.muted', {}, 'Pile')),
        h('div.badge', { 'data-testid': 'cheat-rank' }, claim ? `Claimed: ${rankPlural(claim.rank)}` : `Now: ${rankPlural(v.rank)}`),
      ),
      h('div.event', { 'data-testid': 'event' }, lastText),
      last?.kind === 'called' ? h('div.reveal', { 'data-testid': 'reveal' }, last.cards.map((c) => cardFace(c, { small: true }))) : null,
      claim && !mayCall ? h('div.muted', {}, `Waiting for ${claim.waiting.map((s) => name(ctx, s)).join(', ')} to call or let it go`) : null,
      mayCall && claim
        ? h(
            'div.row.actions',
            {},
            h('button.primary.big', { 'data-testid': 'call', onclick: () => ctx.act({ type: 'call' }) }, 'Cheat!'),
            h('button.big', { 'data-testid': 'pass', onclick: () => ctx.act({ type: 'pass' }) }, 'Let it go'),
          )
        : null,
      myPlay && picked.length ? pickTray(hand.filter((c) => picked.includes(c.id)), toggle, () => ctx.local.set('cheat-sel', [])) : null,
      myPlay
        ? h(
            'div.hint',
            { 'data-testid': 'hint' },
            picked.length
              ? h('button.primary', { 'data-testid': 'play', onclick: () => (ctx.local.set('cheat-sel', []), ctx.act({ type: 'play', cards: picked })) }, `Play ${picked.length} as ${rankPlural(v.rank)}`)
              : `Pick 1–${v.maxPlay} cards to play as ${rankPlural(v.rank)}`,
          )
        : null,
      v.hand
        ? h(
            'div.hand',
            { 'data-testid': 'hand' },
            hand.map((c) => cardFace(c, { picked: picked.includes(c.id), onclick: myPlay ? () => toggle(c.id) : undefined })),
          )
        : null,
      v.turnLimit !== null ? h('div.muted', {}, `Play ${v.claims + 1} of ${v.turnLimit}`) : null,
    );
  },
};

// ---------------- Uno ----------------
const UNO_SYMBOL: Record<string, string> = { skip: '⊘', reverse: '⇄', draw2: '+2', wild: 'W', wild4: '+4' };
const UNO_NAME: Record<string, string> = { skip: 'Skip', reverse: 'Reverse', draw2: 'Draw Two', wild: 'Wild', wild4: 'Wild Draw Four' };
const COLOR_NAME: Record<UnoColor, string> = { red: 'Red', yellow: 'Yellow', green: 'Green', blue: 'Blue' };
const unoLabel = (c: UnoCard) => `${c.color ? `${COLOR_NAME[c.color]} ` : ''}${UNO_NAME[c.value] ?? c.value}`;

function unoCard(c: UnoCard, opts: { onclick?: (() => void) | undefined; selected?: boolean; dim?: boolean; testid?: string } = {}): HTMLElement {
  const cls = ['card', 'uno', `uno-${c.color ?? 'wild'}`, opts.selected ? 'selected' : '', opts.dim ? 'dim' : ''].filter(Boolean).join('.');
  const sym = UNO_SYMBOL[c.value] ?? c.value;
  return h(
    `${opts.onclick ? 'button' : 'div'}.${cls}`,
    { 'data-card': c.id, 'data-testid': opts.testid, 'aria-label': unoLabel(c), onclick: opts.onclick ? () => opts.onclick?.() : undefined },
    h('span.corner', {}, sym),
    h('span.pip', {}, sym),
  );
}

const unoUi: GameUi = {
  id: 'uno',
  privateHands: true,
  seatInfo: (ctx, s) => {
    const v = ctx.msg.view as UnoView;
    const n = v.handCounts[s] ?? 0;
    return `${n} card${n === 1 ? '' : 's'}${n === 1 ? ' · UNO!' : ''}${v.target !== null ? ` · ${v.scores[s] ?? 0} pts` : ''}`;
  },
  render(ctx) {
    const v = ctx.msg.view as UnoView;
    const hand = v.hand ?? [];
    const wildFor = ctx.local.get<string>('uno-wild');
    const callUno = !!ctx.local.get<boolean>('uno-call');
    const canDraw = ctx.myTurn && !v.pending;
    const play = (card: UnoCard, color?: UnoColor) => {
      ctx.local.set('uno-wild', undefined);
      ctx.local.set('uno-call', false);
      ctx.act({ type: 'play', card: card.id, ...(color ? { color } : {}), ...(callUno ? { uno: true } : {}) });
    };
    const ev = (e: UnoEvent | null): string => {
      if (!e) return `${name(ctx, v.turn)} starts. Match the colour or symbol.`;
      if (e.kind === 'round') return `${name(ctx, e.winner)} won round ${e.round} (+${e.points} points). New deal!`;
      if (e.kind === 'pass') return `${name(ctx, e.seat)} kept the card.`;
      if (e.kind === 'draw') return `${name(ctx, e.seat)} drew a card${e.card ? `: ${unoLabel(e.card)}` : ''}${e.playable ? '' : '.'}`;
      return (
        `${name(ctx, e.seat)} played ${unoLabel(e.card)}${e.card.color ? '' : ` — ${COLOR_NAME[e.color]}`}.` +
        (e.victim !== null ? ` ${name(ctx, e.victim)} ${e.victim === ctx.you ? 'draw' : 'draws'} ${e.drew}.` : '') +
        (e.uno ? ' UNO!' : '') +
        (e.penalty ? ' Forgot to call UNO — drew 2.' : '')
      );
    };
    const pendingCard = v.pending ? hand.find((c) => c.id === v.pending) : undefined;
    return h(
      'div.uno-table',
      {},
      others(ctx, v.handCounts),
      h(
        'div.row.uno-center',
        {},
        h(
          `${canDraw ? 'button' : 'div'}.ocean`,
          { 'data-testid': 'uno-draw', onclick: canDraw ? () => ctx.act({ type: 'draw' }) : undefined },
          pile(v.drawCount),
          h('div.muted', {}, canDraw ? 'Tap to draw' : 'Draw'),
        ),
        h('div.uno-top', {}, unoCard(v.top, { testid: 'uno-top' }), h(`div.uno-chip.uno-${v.color}`, { 'data-testid': 'uno-color' }, COLOR_NAME[v.color])),
        h('div.muted', {}, v.direction === 1 ? '↻' : '↺'),
      ),
      h('div.event', { 'data-testid': 'event' }, ev(v.last)),
      pendingCard
        ? h('div.hint', {}, `You drew ${unoLabel(pendingCard)} — play it or `, h('button', { 'data-testid': 'uno-keep', onclick: () => ctx.act({ type: 'pass' }) }, 'Keep it'))
        : ctx.myTurn
          ? h('div.hint', { 'data-testid': 'hint' }, v.playable.length ? 'Tap a card to play it, or draw.' : 'Nothing matches — draw a card.')
          : null,
      wildFor && ctx.myTurn
        ? h(
            'div.row.actions',
            { 'data-testid': 'uno-colors' },
            (['red', 'yellow', 'green', 'blue'] as const).map((col) =>
              h(`button.uno-chip.uno-${col}`, { 'data-testid': `uno-color-${col}`, onclick: () => play(hand.find((c) => c.id === wildFor) as UnoCard, col) }, COLOR_NAME[col]),
            ),
          )
        : null,
      ctx.myTurn && hand.length === 2 && v.playable.length
        ? h(`button.big${callUno ? '.primary' : ''}`, { 'data-testid': 'uno-call', onclick: () => ctx.local.set('uno-call', !callUno) }, callUno ? 'UNO! called' : 'Call UNO!')
        : null,
      v.hand
        ? h(
            'div.hand',
            { 'data-testid': 'hand' },
            hand.map((c) => {
              const ok = v.playable.includes(c.id);
              return unoCard(c, {
                selected: c.id === wildFor,
                dim: ctx.myTurn && !ok,
                onclick: ok ? () => (c.color === null ? ctx.local.set('uno-wild', c.id === wildFor ? undefined : c.id) : play(c)) : undefined,
              });
            }),
          )
        : null,
      v.target !== null ? h('div.muted', {}, `Round ${v.round} · first to ${v.target}`) : null,
    );
  },
};

// ---------------- Card Table (free play) ----------------
const SUIT_ORDER = 'SHDC';
const RANK_ORDER = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A', 'JK'];
function sortHand(hand: readonly Card[], by: string | undefined): readonly Card[] {
  if (by !== 'suit' && by !== 'rank') return hand;
  const suit = (c: Card) => SUIT_ORDER.indexOf(c.suit ?? 'X');
  const rank = (c: Card) => RANK_ORDER.indexOf(c.rank);
  return [...hand].sort((a, b) => (by === 'suit' ? suit(a) - suit(b) || rank(a) - rank(b) : rank(a) - rank(b) || suit(a) - suit(b)));
}

const tableUi: GameUi = {
  id: 'table',
  privateHands: true,
  seatInfo: (ctx, s) => {
    const v = ctx.msg.view as TableView;
    return `${v.handCounts[s] ?? 0} in hand · ${v.wonCounts[s] ?? 0} taken${s === v.dealer ? ' · dealer' : ''}`;
  },
  render(ctx) {
    const v = ctx.msg.view as TableView;
    const me = ctx.mySeat;
    const canPlay = me !== null && !v.result && (v.turns === 'free' || v.turn === me);
    const isDealer = me !== null && me === v.dealer && !v.result;
    // Hand order is a per-player preference that survives pass & play hand-offs.
    const sortKey = `keep:table-sort:${me}`;
    const orderKey = `keep:table-order:${me}`;
    const sortBy = ctx.local.get<string>(sortKey);
    const hand = sortBy === 'custom' ? applyOrder(v.hand ?? [], ctx.local.get<string[]>(orderKey)) : sortHand(v.hand ?? [], sortBy);
    const move = (m: Move) => {
      ctx.local.set(orderKey, moveBlock(hand.map((c) => c.id), picked, m));
      ctx.local.set(sortKey, 'custom');
    };
    const picked = (ctx.local.get<string[]>('table-sel') ?? []).filter((id) => hand.some((c) => c.id === id));
    const send = (a: { type: string } & Record<string, unknown>) => {
      ctx.local.set('table-sel', []);
      ctx.local.set('table-finish', false);
      ctx.act(a);
    };
    const n = (count: number) => `${count} card${count === 1 ? '' : 's'}`;
    const text = (e: TableEvent): string => {
      const who = name(ctx, e.seat);
      switch (e.kind) {
        case 'gather': return `${who} gathered and shuffled the deck.`;
        case 'deal': return `${who} dealt ${n(e.each)} each${e.left ? ` (${e.left} left in the deck)` : ''}.`;
        case 'draw': return `${who} drew ${e.card ? cardLabel(e.card) : 'a card'} from the ${e.from}.`;
        case 'play': return `${who} played ${e.faceDown ? `${n(e.count)} face down` : e.cards.map(cardLabel).join(' ')}.`;
        case 'discard': return `${who} discarded ${e.cards.map(cardLabel).join(' ')}.`;
        case 'give': return `${who} gave ${n(e.count)} to ${name(ctx, e.to)}.`;
        case 'take': return `${who} took the middle (${n(e.count)}) into ${e.seat === ctx.you ? 'your' : 'their'} ${e.into === 'won' ? 'pile' : 'hand'}.`;
        case 'flip': return `${who} flipped ${n(e.count)} face up.`;
        case 'turn': return `${who} passed the turn to ${name(ctx, e.to)}.`;
        case 'dealer': return `${who} passed the deal to ${name(ctx, e.to)}.`;
        case 'finish': return `${who} ended the game.`;
      }
    };
    const others = ctx.names.map((_, s) => s).filter((s) => s !== me);
    const dealCount = ctx.local.get<string>('table-deal') ?? 'all';
    const finishing = !!ctx.local.get<boolean>('table-finish');
    return h(
      'div.cardtable',
      {},
      // End turn sits at the top so it's never below a long hand.
      canPlay && v.turns === 'ordered'
        ? h(
            'div.row.actions.wrap.turnbar',
            { 'data-testid': 'table-turnbar' },
            h('button.primary', { 'data-testid': 'table-end-turn', onclick: () => send({ type: 'endTurn' }) }, `End turn → ${name(ctx, (v.turn + 1) % ctx.names.length)}`),
            others.length > 1 ? others.map((s) => h('button.link', { 'data-testid': `table-turn-${s}`, onclick: () => send({ type: 'endTurn', to: s }) }, `→ ${name(ctx, s)}`)) : null,
          )
        : null,
      h(
        'div.others',
        { 'data-testid': 'others' },
        others.map((s) =>
          h(
            `div.other${v.turns === 'ordered' && v.turn === s ? '.active' : ''}`,
            { 'data-testid': `other-${s}` },
            h('div.other-name', {}, `${ctx.names[s] ?? `Seat ${s + 1}`}${s === v.dealer ? ' (dealer)' : ''}`),
            h('div.mini-fan', {}, Array.from({ length: Math.min(v.handCounts[s] ?? 0, 12) }, () => cardBack({ small: true }))),
            (v.wonCounts[s] ?? 0) > 0 ? h('div.muted', {}, `${v.wonCounts[s]} taken`) : null,
          ),
        ),
      ),
      h(
        'div.row.table-piles',
        {},
        h(
          `${canPlay && v.deckCount ? 'button' : 'div'}.ocean`,
          { 'data-testid': 'table-deck', onclick: canPlay && v.deckCount ? () => send({ type: 'draw', from: 'deck' }) : undefined },
          pile(v.deckCount),
          h('div.muted', {}, canPlay && v.deckCount ? 'Draw' : 'Deck'),
        ),
        h(
          `${canPlay && v.discard.length ? 'button' : 'div'}.ocean.plain`,
          { 'data-testid': 'table-discard', onclick: canPlay && v.discard.length ? () => send({ type: 'draw', from: 'discard' }) : undefined },
          v.discard[0] ? cardFace(v.discard[0]) : h('div.card.empty'),
          h('div.muted', {}, 'Discard'),
        ),
      ),
      h(
        'div.table-center',
        { 'data-testid': 'table-center' },
        v.center.length
          ? v.center.map((p, i) => h('div.played', {}, p.card ? cardFace(p.card, { small: true, testid: `center-${i}` }) : cardBack({ small: true, testid: `center-${i}` }), h('div.muted', {}, name(ctx, p.by))))
          : h('div.muted', {}, 'The middle of the table'),
      ),
      canPlay && v.center.length
        ? h(
            'div.row.actions',
            {},
            h('button', { 'data-testid': 'table-take', onclick: () => send({ type: 'take' }) }, 'Take to my pile'),
            h('button', { 'data-testid': 'table-take-hand', onclick: () => send({ type: 'take', into: 'hand' }) }, 'Take into hand'),
            v.center.some((p) => !p.card) ? h('button', { 'data-testid': 'table-flip', onclick: () => send({ type: 'flip' }) }, 'Flip face up') : null,
          )
        : null,
      h('div.event', { 'data-testid': 'event' }, v.log.length ? v.log.slice(-3).map((e) => h('div', {}, text(e))) : 'No rules here — agree on a game. The dealer starts by dealing.'),
      picked.length ? pickTray(hand.filter((c) => picked.includes(c.id)), (id) => ctx.local.set('table-sel', picked.filter((x) => x !== id)), () => ctx.local.set('table-sel', [])) : null,
      picked.length && hand.length > 1
        ? h(
            'div.row.actions.mover',
            { 'data-testid': 'table-move' },
            h('span.muted', {}, picked.length > 1 ? 'Move together:' : 'Move:'),
            ([['start', '⇤'], [-1, '◀'], [1, '▶'], ['end', '⇥']] as const).map(([m, label]) =>
              h('button', { 'data-testid': `table-move-${m}`, 'aria-label': `Move ${m}`, onclick: () => move(m) }, label),
            ),
          )
        : null,
      canPlay && picked.length
        ? h(
            'div.row.actions.wrap',
            { 'data-testid': 'table-selected' },
            h('button.primary', { 'data-testid': 'table-play', onclick: () => send({ type: 'play', cards: picked }) }, `Play ${picked.length} face up`),
            h('button', { 'data-testid': 'table-play-down', onclick: () => send({ type: 'play', cards: picked, faceDown: true }) }, 'Face down'),
            h('button', { 'data-testid': 'table-discard-sel', onclick: () => send({ type: 'discard', cards: picked }) }, 'Discard'),
            others.map((s) => h('button', { 'data-testid': `table-give-${s}`, onclick: () => send({ type: 'give', cards: picked, to: s }) }, `Give to ${name(ctx, s)}`)),
          )
        : null,
      v.hand
        ? h(
            'div.hand',
            { 'data-testid': 'hand' },
            hand.map((c) =>
              cardFace(c, {
                picked: picked.includes(c.id),
                // Picking works any time so you can arrange your hand while others play.
                onclick: () => ctx.local.set('table-sel', picked.includes(c.id) ? picked.filter((x) => x !== c.id) : [...picked, c.id]),
              }),
            ),
          )
        : null,
      v.hand && v.hand.length > 1
        ? h(
            'div.row.actions',
            {},
            h('span.muted', {}, 'Sort:'),
            (['none', 'suit', 'rank', 'custom'] as const).map((k) =>
              h(
                `button.link${(sortBy ?? 'none') === k ? '.on' : ''}`,
                {
                  'data-testid': `table-sort-${k}`,
                  // Custom starts from whatever order is on screen now.
                  onclick: () => (k === 'custom' && sortBy !== 'custom' && ctx.local.set(orderKey, hand.map((c) => c.id)), ctx.local.set(sortKey, k)),
                },
                { none: 'As dealt', suit: 'By suit', rank: 'By rank', custom: 'My order' }[k],
              ),
            ),
          )
        : null,
      v.won && v.won.length ? h('div.muted', { 'data-testid': 'table-won' }, `Your pile: ${v.won.length} card${v.won.length === 1 ? '' : 's'}`) : null,
      isDealer
        ? h(
            'div.panel.dealer',
            { 'data-testid': 'table-dealer' },
            h('strong', {}, 'Dealer'),
            v.deckCount
              ? h(
                  'div.row',
                  {},
                  h(
                    'select',
                    { 'data-testid': 'table-deal-count', onchange: (e: Event) => ctx.local.set('table-deal', (e.target as HTMLSelectElement).value) },
                    ['all', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '13'].map((k) => h('option', { value: k, selected: k === dealCount }, k === 'all' ? 'All cards' : `${k} each`)),
                  ),
                  h('button.primary', { 'data-testid': 'table-deal', onclick: () => send({ type: 'deal', count: dealCount === 'all' ? 'all' : Number(dealCount) }) }, 'Deal'),
                )
              : null,
            h(
              'div.row',
              {},
              confirmButton('button', { 'data-testid': 'table-gather' }, 'Gather & shuffle', 'Tap again to gather', () => send({ type: 'gather' })),
              h('button', { 'data-testid': 'table-finish', onclick: () => ctx.local.set('table-finish', !finishing) }, 'End game…'),
            ),
            others.length
              ? h(
                  'div.row.actions.wrap',
                  {},
                  h('button', { 'data-testid': 'table-pass-deal', onclick: () => send({ type: 'passDeal' }) }, `Pass the deal → ${name(ctx, (v.dealer + 1) % ctx.names.length)}`),
                  others.length > 1 ? others.map((s) => h('button.link', { 'data-testid': `table-deal-to-${s}`, onclick: () => send({ type: 'passDeal', to: s }) }, `→ ${name(ctx, s)}`)) : null,
                )
              : null,
            finishing
              ? h(
                  'div.row.actions.wrap',
                  {},
                  h('span.muted', {}, 'Who won?'),
                  ctx.names.map((_, s) => h('button', { 'data-testid': `table-winner-${s}`, onclick: () => send({ type: 'finish', winners: [s] }) }, name(ctx, s))),
                  h('button.link', { 'data-testid': 'table-no-winner', onclick: () => send({ type: 'finish', winners: [] }) }, 'No winner'),
                )
              : null,
          )
        : null,
    );
  },
};

// ---------------- Texas Hold'em ----------------
const ROLE_LABEL = { button: 'DEALER', smallBlind: 'SMALL BLIND', bigBlind: 'BIG BLIND' } as const;

function holdemRoles(v: HoldemView, seat: Seat): ('button' | 'smallBlind' | 'bigBlind')[] {
  const p = v.positions;
  return (['button', 'smallBlind', 'bigBlind'] as const).filter((k) => p[k] === seat);
}

/** Check/Call, Fold, a bounded Bet/Raise amount and All-in for the player whose turn it is. */
function holdemBetting(ctx: TableCtx, v: HoldemView): HTMLElement | null {
  if (ctx.mySeat === null || v.toAct !== ctx.mySeat || v.street === 'showdown') return null;
  const canRaise = v.minRaiseTo > 0;
  const sig = `${v.handNo}-${v.street}-${v.currentBet}`;
  const stored = ctx.local.get<{ sig: string; amt: number }>('hd-amt');
  const clamp = (n: number) => Math.max(v.minRaiseTo, Math.min(v.maxTo, Math.round(Number.isFinite(n) ? n : v.minRaiseTo)));
  const amt = clamp(stored && stored.sig === sig ? stored.amt : v.minRaiseTo);
  const setAmt = (n: number) => ctx.local.set('hd-amt', { sig, amt: clamp(n) });
  const verb = v.currentBet === 0 ? 'Bet' : 'Raise to';
  return h(
    'div.bet-panel',
    { 'data-testid': 'holdem-actions' },
    h(
      'div.row.actions.wrap',
      {},
      v.toCall > 0
        ? h('button.primary.big', { 'data-testid': 'holdem-call', onclick: () => ctx.act({ type: 'call' }) }, v.toCall >= (v.stacks[ctx.mySeat] ?? 0) ? `Call ${v.toCall} (all-in)` : `Call ${v.toCall}`)
        : h('button.primary.big', { 'data-testid': 'holdem-check', onclick: () => ctx.act({ type: 'check' }) }, 'Check'),
      h('button.big', { 'data-testid': 'holdem-fold', onclick: () => ctx.act({ type: 'fold' }) }, 'Fold'),
    ),
    canRaise && v.minRaiseTo < v.maxTo
      ? h(
          'div.row.raise-row',
          {},
          h('button', { 'data-testid': 'holdem-minus', 'aria-label': 'Less', onclick: () => setAmt(amt - v.bigBlind) }, '−'),
          h('input', {
            type: 'number',
            inputmode: 'numeric',
            'data-testid': 'holdem-amount',
            'aria-label': 'Bet total',
            min: v.minRaiseTo,
            max: v.maxTo,
            step: v.bigBlind,
            value: amt,
            onchange: (e: Event) => setAmt(Number((e.target as HTMLInputElement).value)),
          }),
          h('button', { 'data-testid': 'holdem-plus', 'aria-label': 'More', onclick: () => setAmt(amt + v.bigBlind) }, '+'),
          h('button.primary', { 'data-testid': 'holdem-raise', onclick: () => ctx.act({ type: 'bet', to: amt }) }, `${verb} ${amt}`),
        )
      : null,
    canRaise ? h('button.big', { 'data-testid': 'holdem-allin', onclick: () => ctx.act({ type: 'allin' }) }, `All-in (${v.you === null ? v.maxTo : v.stacks[v.you]} chips)`) : null,
  );
}

const holdemUi: GameUi = {
  id: 'holdem',
  privateHands: true,
  seatInfo: (ctx, s) => {
    const v = ctx.msg.view as HoldemView;
    if (!v.seated[s]) return 'left';
    const tags = holdemRoles(v, s).map((r) => ({ button: 'D', smallBlind: 'SB', bigBlind: 'BB' })[r]);
    return [...tags, v.folded[s] ? 'folded' : '', v.betting ? `${v.stacks[s]} chips` : ''].filter(Boolean).join(' · ') || 'in';
  },
  render(ctx) {
    const v = ctx.msg.view as HoldemView;
    const me = ctx.mySeat;
    const mine = me === null ? [] : holdemRoles(v, me);
    const dealer = me !== null && me === v.positions.button && !v.result;
    const nextDealer = (() => {
      for (let k = 1; k <= v.seated.length; k++) {
        const seat = (v.positions.button + k) % v.seated.length;
        if (v.seated[seat]) return seat;
      }
      return v.positions.button;
    })();
    const STREET_NEXT = { preflop: 'Deal the flop', flop: 'Deal the turn', turn: 'Deal the river', river: 'Showdown', showdown: `Next hand (${name(ctx, nextDealer)} deals)` } as const;
    const chip = (r: 'button' | 'smallBlind' | 'bigBlind', small = false) => h(`span.role-chip.role-${r}${small ? '.small' : ''}`, {}, small ? ({ button: 'D', smallBlind: 'SB', bigBlind: 'BB' })[r] : ROLE_LABEL[r]);
    const winners = new Set(v.hand?.winners ?? []);
    const betting = v.betting;
    const acting = betting && v.street !== 'showdown' ? v.toAct : null;
    const shownBySeat = new Map((v.hand?.shown ?? []).map((e) => [e.seat, e.hand]));
    const handText = v.hand && betting
      ? v.hand.winners
          .map((w) => `${name(ctx, w)} ${w === ctx.you ? 'win' : 'wins'} ${v.hand?.payouts?.[w] ?? 0}${v.hand?.shown.length ? ` with ${shownBySeat.get(w)?.name.toLowerCase() ?? 'the pot'}` : ''}`)
          .join(' · ') + (v.hand.shown.length ? '' : ' — everyone else folded')
      : v.hand
      ? v.hand.shown.length
        ? `${v.hand.winners.map((w) => name(ctx, w)).join(' & ')} ${v.hand.winners.length > 1 ? 'split' : v.hand.winners[0] === ctx.you ? 'win' : 'wins'} with ${shownBySeat.get(v.hand.winners[0] as Seat)?.name.toLowerCase()}`
        : `${name(ctx, v.hand.winners[0] as Seat)} ${v.hand.winners[0] === ctx.you ? 'win' : 'wins'} — everyone else folded`
      : null;
    return h(
      'div.holdem',
      {},
      // Big, unmissable role banner on each player's own phone.
      me !== null && v.seated[me]
        ? h(
            'div.role-banner',
            { 'data-testid': 'holdem-role' },
            mine.length ? mine.map((r) => chip(r)) : h('span.muted', {}, 'No blind this hand'),
            betting
              ? me === acting ? h('span.role-chip.role-act', { 'data-testid': 'holdem-first' }, 'YOUR TURN') : null
              : me === v.positions.firstToAct && v.street !== 'showdown' && !v.folded[me] ? h('span.role-chip.role-act', { 'data-testid': 'holdem-first' }, 'FIRST TO ACT') : null,
          )
        : null,
      h(
        'ul.holdem-players',
        { 'data-testid': 'holdem-players' },
        ctx.names.map((n, s) =>
          h(
            `li${!v.seated[s] ? '.gone' : v.folded[s] ? '.folded' : ''}${winners.has(s) ? '.winner' : ''}`,
            { 'data-testid': `holdem-seat-${s}` },
            h('span.pname', {}, s === ctx.you ? `${n} (you)` : n),
            holdemRoles(v, s).map((r) => chip(r, true)),
            (betting ? s === acting : s === v.positions.firstToAct && v.street !== 'showdown') ? h('span.role-chip.role-act.small', {}, 'acts') : null,
            !v.seated[s] ? h('span.muted', {}, betting && v.stacks[s] === 0 ? 'out' : 'left') : v.folded[s] ? h('span.muted', {}, 'folded') : betting && v.stacks[s] === 0 && v.street !== 'showdown' ? h('span.muted', {}, 'all-in') : null,
            betting ? h('span.chips', {}, h('span.stack', { 'data-testid': `holdem-stack-${s}` }, `${v.stacks[s]}`), v.bets[s] ? h('span.bet', { 'data-testid': `holdem-bet-${s}` }, `bet ${v.bets[s]}`) : null) : null,
            v.shown[s] ? h('span.shown', {}, (v.shown[s] ?? []).map((c) => cardFace(c, { small: true })), h('span.muted', {}, shownBySeat.get(s)?.name ?? '')) : null,
          ),
        ),
      ),
      h(
        'div.board',
        { 'data-testid': 'holdem-board' },
        Array.from({ length: 5 }, (_, i) => (v.board[i] ? cardFace(v.board[i] as Card, { testid: `board-${i}` }) : h('div.card.empty'))),
      ),
      betting && v.street !== 'showdown' ? h('div.pot', { 'data-testid': 'holdem-pot' }, `Pot ${v.pot}`) : null,
      h('div.event', { 'data-testid': 'event' }, handText ?? `Hand ${v.handNo} · ${v.street === 'preflop' ? 'before the flop' : `the ${v.street}`}${betting && v.currentBet ? ` · bet ${v.currentBet}` : ''}`),
      v.hole ? h('div.hand.hole', { 'data-testid': 'hand' }, v.hole.map((c) => cardFace(c))) : null,
      betting ? holdemBetting(ctx, v) : null,
      h(
        'div.row.actions.wrap',
        {},
        betting && me !== null && v.seated[me] && v.street === 'showdown' && !v.result
          ? h('button.primary.big', { 'data-testid': 'holdem-next', onclick: () => ctx.act({ type: 'next' }) }, 'Next hand')
          : null,
        dealer && !betting ? h('button.primary.big', { 'data-testid': 'holdem-next', onclick: () => ctx.act({ type: 'next' }) }, STREET_NEXT[v.street]) : null,
        !betting && me !== null && v.inHand[me] && v.street !== 'showdown' ? h('button.big', { 'data-testid': 'holdem-fold', onclick: () => ctx.act({ type: 'fold' }) }, 'Fold') : null,
        me !== null && v.seated[me] && !v.result
          ? confirmButton('button.link', { 'data-testid': 'holdem-leave' }, 'Leave table', 'Tap again to leave', () => ctx.act({ type: 'leave' }))
          : null,
      ),
      !betting && !dealer && !v.result ? h('div.muted', {}, `${name(ctx, v.positions.button)} deals.`) : null,
      betting && acting !== null && acting !== me ? h('div.muted', {}, `Waiting for ${name(ctx, acting)}.`) : null,
    );
  },
};

export const GAME_UIS: Readonly<Record<string, GameUi>> = {
  [warUi.id]: warUi,
  [goFishUi.id]: goFishUi,
  [oldMaidUi.id]: oldMaidUi,
  [cheatUi.id]: cheatUi,
  [unoUi.id]: unoUi,
  [tableUi.id]: tableUi,
  [holdemUi.id]: holdemUi,
  [rummy500Ui.id]: rummy500Ui,
  [ginUi.id]: ginUi,
  [crazyEightsUi.id]: crazyEightsUi,
  [euchreUi.id]: euchreUi,
  [spadesUi.id]: spadesUi,
  [heartsUi.id]: heartsUi,
  [liarsDiceUi.id]: liarsDiceUi,
  [farkleUi.id]: farkleUi,
  [yahtzeeUi.id]: yahtzeeUi,
  [mastermindUi.id]: mastermindUi,
  [checkersUi.id]: checkersUi,
  [chessUi.id]: chessUi,
  [battleshipUi.id]: battleshipUi,
  [candylandUi.id]: candylandUi,
  [chutesUi.id]: chutesUi,
  [sorryUi.id]: sorryUi,
  [parcheesiUi.id]: parcheesiUi,
};
